import { UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { MsalProvider } from '@azure/msal-react';
import { Field } from '@lucidtech/las-sdk-browser';
import { memo, useCallback } from 'react';

import InfoIcon from '@/assets/info-icon.svg?react';
import { Button, Flex, Heading, Label, Loading, Radio, RadioGroup, Switch, Text } from '@/components/core';
import { ExcelLogo } from '@/components/logos';
import { AUTH_SCOPES, EXTENDED_AUTH_SCOPES, msalInstance } from '@/connectors/msgraph';
import { useGetAgentFromPath } from '@/hooks';
import { toConnectionId, toModelId, useDeleteConnection, useGetConnection, useGetModel } from '@/hooks/api';
import { useTriggerStore } from '@/store';
import { findModelId } from '@/utils';

import { FieldMapper } from './FieldMapper';
import { FileSelector } from './FileSelector';
import { TableSelector } from './TableSelector';

const createFieldToIndexMap = (fields: Record<string, Field>) => {
  let idx = 0;

  const result: Record<string, number> = {};
  for (const fieldId of Object.keys(fields).sort()) {
    if (fields[fieldId].type !== 'table') {
      result[fieldId] = idx;
      idx += 1;
    } else {
      const subfields = fields[fieldId].fields as Record<string, Field>;
      for (const subfieldId of Object.keys(subfields).sort()) {
        result[subfieldId] = idx;
        idx += 1;
      }
    }
  }

  return result;
};

export type ExportExcelConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const IExportExcelConfiguration = ({}: ExportExcelConfigurationProps) => {
  const action = useTriggerStore((state) => state.action);
  const updateAction = useTriggerStore((state) => state.updateAction);
  const { data: agent } = useGetAgentFromPath();
  const { data: model } = useGetModel(toModelId(findModelId(agent)));

  const { instance } = useMsal();
  const { data: connection, isLoading } = useGetConnection(toConnectionId(action?.connectionId));
  const { mutateAsync: deleteConnection } = useDeleteConnection();

  const handleLogin = useCallback(async () => {
    if (action.actionId && agent?.agentId) {
      instance
        ?.loginRedirect({
          scopes: AUTH_SCOPES,
          state: JSON.stringify({
            actionId: action.actionId,
            agentId: agent.agentId,
            scopesRequested: AUTH_SCOPES,
          }),
        })
        .catch(console.error);
    }
  }, [action.actionId, agent?.agentId, instance]);

  const handleLogout = useCallback(() => {
    instance
      ?.logoutPopup()
      ?.catch(console.error)
      ?.then(() => {
        deleteConnection(toConnectionId(action?.connectionId));
        updateAction({
          connectionId: null,
          config: {},
        });
      });
  }, [action?.connectionId, deleteConnection, instance, updateAction]);

  const requestElevatedPermissions = useCallback(async () => {
    if (action.actionId && agent?.agentId) {
      instance
        ?.loginRedirect({
          scopes: EXTENDED_AUTH_SCOPES,
          state: JSON.stringify({
            actionId: action.actionId,
            agentId: agent.agentId,
            scopesRequested: EXTENDED_AUTH_SCOPES,
          }),
        })
        .catch(console.error);
    }
  }, [action.actionId, agent?.agentId, instance]);

  const setSaveOriginalDocument = useCallback(
    (checked: boolean) => {
      updateAction((state) => ({ config: { ...state.config, saveOriginalDocument: checked } }));
    },
    [updateAction]
  );

  const setCreateNewWorkbook = useCallback(
    (val: string) => {
      const checked = val === 'separate' ? true : false;
      const fieldToIndexMap = createFieldToIndexMap(model?.fieldConfig || {});
      updateAction((state) => ({
        config: {
          ...state.config,
          createNewWorkbook: checked,
          fieldToIndexMap: checked ? fieldToIndexMap : {},
          driveId: null,
          driveItemId: null,
          siteId: null,
        },
      }));
    },
    [model?.fieldConfig, updateAction]
  );

  const createNewWorkbook = Boolean(action?.config?.createNewWorkbook);

  return (
    <div className="flex flex-col gap-2 p-4">
      {!agent || !action || isLoading ? (
        <Loading label="Loading" />
      ) : (
        connection && (
          <Flex col variant="plain" className="gap-md">
            <Flex className="p-sm gap-sm items-center rounded-md border-1 border-gray-200 bg-gray-50">
              <ExcelLogo size="xl" />

              <Text className="grow">Connected to Excel</Text>

              <Button variant="secondary" onPress={handleLogout}>
                <Text>Disconnect</Text>
              </Button>
            </Flex>

            <Flex col className="gap-md">
              <RadioGroup onChange={setCreateNewWorkbook} defaultValue={createNewWorkbook ? 'separate' : 'single'}>
                <Radio value="separate">
                  <Text slot="description">Export to separate workbooks</Text>
                </Radio>
                <Radio value="single">
                  <Text slot="description">Export to single workbook</Text>
                </Radio>
              </RadioGroup>

              <FileSelector
                connection={connection}
                label={createNewWorkbook ? 'Folder' : 'Workbook'}
                description={createNewWorkbook ? 'Choose a folder where the workbooks will be saved.' : undefined}
                itemType={createNewWorkbook ? 'folder' : 'workbook'}
                requestElevatedPermission={requestElevatedPermissions}
              />

              <Switch
                onChange={setSaveOriginalDocument}
                defaultSelected={Boolean(action?.config?.saveOriginalDocument)}
              >
                <Label className="text-sm">Save the original document in the same folder as the workbook.</Label>
              </Switch>

              {!createNewWorkbook && (
                <>
                  <TableSelector connection={connection} />
                  <FieldMapper connection={connection} />
                </>
              )}
            </Flex>
          </Flex>
        )
      )}

      {!connection && !isLoading && (
        <UnauthenticatedTemplate>
          <Flex className="p-sm gap-sm items-center rounded-md border-1 border-gray-200 bg-gray-50">
            <ExcelLogo size="xl" />

            <Text className="grow">Connect to Excel</Text>

            <Button variant="primary" onPress={handleLogin}>
              <Text>Connect</Text>
            </Button>
          </Flex>

          <Flex variant="primary" className="p-md gap-sm content-start rounded-md border-1">
            <InfoIcon className="text-primary-500 mt-[0.1rem] size-[0.85rem]" />

            <Flex col className="border-0">
              <Heading size="xs">Use a work or school account</Heading>
              <Text size="xs">Personal Microsoft accounts are not currently supported.</Text>
            </Flex>
          </Flex>
        </UnauthenticatedTemplate>
      )}
    </div>
  );
};

const IExportExcelConfigurationWrapper = ({ ...rest }: ExportExcelConfigurationProps) => {
  return (
    <MsalProvider instance={msalInstance}>
      <IExportExcelConfiguration {...rest} />
    </MsalProvider>
  );
};

export const ExportExcelConfiguration = memo(IExportExcelConfigurationWrapper) as typeof IExportExcelConfiguration;
